import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { directionOf, secondary, t } from '@/lib/locale';
import { useT } from '@/lib/i18n';
import type { Localized } from '@/lib/api';

/**
 * ONE OF THE TWO DATA-DRIVEN CORES (build/05 §"The two data-driven cores").
 *
 * `RENDERERS` is a `Record<itemType, FC>` and `RunAssessment` renders any item
 * with `RENDERERS[item.itemType]`. There is no `if (itemType === …)` anywhere
 * else in the console, and no branch on MODEL anywhere at all — a module's model
 * decides which item types it uses, and the types decide which renderer runs.
 *
 * That is what makes the Phase-6 architecture check pass: switching module
 * changes the item types, the plugin and the result viz, and the only code that
 * "knows" is this map and the viz map. Adding an eighth item type is one entry
 * here.
 *
 * WHAT A RENDERER MAY SEE. Its props are a `DeliveryItem` — key-stripped by the
 * server. There is deliberately no `dimension`, `isCorrect` or `reverseKeyed` to
 * render even by accident, because the payload never carried them.
 *
 * TIMING. Each renderer records when the item was displayed; the harness pairs
 * that with the answer time to produce `responseTimeMs`, which feeds the
 * `speeding` quality flag. It is QC metadata and never touches a score.
 */

export type ResponseValue = number | string | string[];

export interface DeliveryItem {
  itemId: string;
  position: number;
  itemType: string;
  prompt: { en: string; ar: string };
  options?: Array<{ optionId: string; label: { en: string; ar: string } }>;
  scale?: { min: number; max: number } | null;
  savedResponse?: ResponseValue | null;
}

export interface ItemRendererProps {
  item: DeliveryItem;
  value: ResponseValue | null;
  onChange: (value: ResponseValue) => void;
  /**
   * THE LOCALE THIS RUN IS BEING ADMINISTERED IN — the instance's pinned
   * `administeredLocale`, passed down rather than read from a context.
   *
   * A context would be an ambient "current language", and the hazard here is
   * precisely an operator's display preference silently deciding what a
   * candidate is shown. Threading it through props keeps every renderer
   * answerable to the *instance*, and makes a renderer that ignores it visible
   * as one that ignored an argument.
   */
  locale: string;
}

/**
 * The label in the administration language, with the other beneath it.
 *
 * BOTH are shown because this is a QA HARNESS and not the candidate experience
 * (PRD §2.2): an operator verifying an Arabic administration needs to see what
 * the candidate sees *and* to know what they are looking at. What changed is
 * which one leads — it used to be English, always, whatever the run was pinned
 * to.
 *
 * Each line carries its own `dir`, because direction belongs to a language and
 * not to the page. The second line is omitted entirely when `secondary()`
 * returns nothing — either the field is untranslated, or `t()` already fell back
 * to it, and rendering the same string twice in two styles reads as a bug.
 */
function BilingualLabel({ text, locale }: { text: Localized; locale: string }) {
  const other = secondary(text, locale);
  const primaryDir = directionOf(locale);

  return (
    <>
      <span className="block" dir={primaryDir}>{t(text, locale)}</span>
      {other && (
        <span
          className="text-muted-foreground block text-sm"
          dir={primaryDir === 'rtl' ? 'ltr' : 'rtl'}
        >
          {other}
        </span>
      )}
    </>
  );
}

/* ----------------------------------------------------------------- scalars */

function ScaleChoice({ item, value, onChange, locale }: ItemRendererProps) {
  const min = item.scale?.min ?? 1;
  const max = item.scale?.max ?? 5;
  const points = Array.from({ length: max - min + 1 }, (_, i) => min + i);

  return (
    <div
      className="flex flex-wrap gap-2"
      role="radiogroup"
      aria-label={t(item.prompt, locale)}
    >
      {points.map((point) => {
        const selected = value === point;
        return (
          <Button
            key={point}
            type="button"
            role="radio"
            aria-checked={selected}
            variant={selected ? 'default' : 'outline'}
            className="min-w-12"
            onClick={() => onChange(point)}
          >
            {point}
          </Button>
        );
      })}
    </div>
  );
}

/**
 * Slider — the same scale, but 0–100 rather than 1–5, so buttons would be 101
 * targets. A native range input is the accessible control for a continuous
 * scale and needs no custom keyboard handling.
 */
function SliderItem({ item, value, onChange, locale }: ItemRendererProps) {
  const tRun = useT(locale);
  const min = item.scale?.min ?? 0;
  const max = item.scale?.max ?? 100;
  const answered = typeof value === 'number';
  const current = answered ? value : Math.round((min + max) / 2);

  return (
    <div className="space-y-2">
      <input
        type="range"
        min={min}
        max={max}
        value={current}
        aria-label={t(item.prompt, locale)}
        /**
         * A range input always has a thumb somewhere, so an untouched one used
         * to sit at the midpoint while the readout below said `—`: the control
         * showed an answer the platform had not recorded. On a psychometric
         * instrument that is worse than untidy — it biases toward the midpoint,
         * and a candidate who reads the thumb as their answer leaves the item
         * blank. S6 found it the first time anyone drew this renderer with real
         * data.
         *
         * The thumb still starts centred, because there is nowhere honest to put
         * it, but the control now says it is waiting rather than implying 50.
         */
        aria-valuetext={answered ? String(current) : tRun('run.slider.unanswered')}
        onChange={(e) => onChange(Number(e.target.value))}
        className={cn('w-full', !answered && 'opacity-60')}
      />
      <div className="text-muted-foreground flex justify-between text-xs">
        <span>{min}</span>
        <span className={cn('font-medium', answered ? 'text-foreground' : 'text-muted-foreground')}>
          {answered ? current : tRun('run.slider.unanswered')}
        </span>
        <span>{max}</span>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- choices */

/** One-of-N. Serves forced_choice, mcq, sjt and bars — all "pick one option". */
function SingleChoice({ item, value, onChange, locale }: ItemRendererProps) {
  return (
    <div className="space-y-2" role="radiogroup" aria-label={t(item.prompt, locale)}>
      {(item.options ?? []).map((option) => {
        const selected = value === option.optionId;
        return (
          <button
            key={option.optionId}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.optionId)}
            className={cn(
              'w-full rounded-md border p-3 text-start transition',
              selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
            )}
          >
            <BilingualLabel text={option.label} locale={locale} />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Ranking — the answer is an ORDER, not a selection, so the value is the full
 * list of option ids. Click to append; click again to remove and renumber.
 *
 * Deliberately not drag-and-drop: a drag interface needs a keyboard equivalent
 * to be usable at all, and click-to-rank already is one.
 */
function RankingItem({ item, value, onChange, locale }: ItemRendererProps) {
  /**
   * `useT(locale)` — the RUN's locale, not the operator's.
   *
   * This is the only instruction a candidate reads from us rather than from the
   * item bank, and it must be in the language the run was pinned to. Calling
   * `useT()` here would make an Arabic administration issue its one instruction
   * in whatever the operator happens to read.
   */
  const tRun = useT(locale);
  const order = Array.isArray(value) ? value : [];

  const toggle = (optionId: string) => {
    onChange(order.includes(optionId) ? order.filter((id) => id !== optionId) : [...order, optionId]);
  };

  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-sm" dir={directionOf(locale)}>
        {tRun('run.rankingHint')}
      </p>
      {(item.options ?? []).map((option) => {
        const rank = order.indexOf(option.optionId);
        const ranked = rank >= 0;
        return (
          <button
            key={option.optionId}
            type="button"
            aria-pressed={ranked}
            onClick={() => toggle(option.optionId)}
            className={cn(
              'flex w-full items-center gap-3 rounded-md border p-3 text-start transition',
              ranked ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
            )}
          >
            <span
              className={cn(
                'flex size-7 shrink-0 items-center justify-center rounded-full border text-sm font-medium',
                ranked && 'bg-primary text-primary-foreground border-transparent',
              )}
              aria-hidden
            >
              {ranked ? rank + 1 : ''}
            </span>
            <span>
              <BilingualLabel text={option.label} locale={locale} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * All seven launch item types (build/05 §F, contract `LAUNCH_ITEM_TYPES`).
 *
 * Four of them share `SingleChoice` because "pick one option" is genuinely the
 * same interaction — what differs between mcq, sjt, bars and forced_choice is
 * how the SERVER scores the choice, and the whole point of the key-strip is that
 * the candidate cannot tell them apart. Giving each a bespoke component would be
 * inventing a difference the payload deliberately removed.
 */
export const RENDERERS: Record<string, React.FC<ItemRendererProps>> = {
  likert: ScaleChoice,
  slider: SliderItem,
  forced_choice: SingleChoice,
  ranking: RankingItem,
  mcq: SingleChoice,
  sjt: SingleChoice,
  bars: SingleChoice,
};

/**
 * An item type with no renderer is a bug, and says so rather than rendering blank.
 *
 * Addressed to whoever is fixing it, so it follows the OPERATOR's language and
 * names the map to edit. It is not part of the administration — a candidate
 * seeing this has already met a defect.
 */
export function UnsupportedItem({ item }: { item: DeliveryItem }) {
  const t = useT();
  return (
    <p className="text-destructive text-sm">
      {t('run.noRenderer', { type: item.itemType })}
    </p>
  );
}

/** True when `value` is a complete answer for this item. */
export function isAnswered(item: DeliveryItem, value: ResponseValue | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  if (item.itemType === 'ranking') {
    // A partial ranking is not an answer — every option must be placed.
    return Array.isArray(value) && value.length === (item.options?.length ?? 0);
  }
  if (Array.isArray(value)) return value.length > 0;
  return value !== '';
}
